import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { article, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/guides/zapier')({
  component: ZapierGuidePage,
  head: () => ({
    meta: [
      { title: 'Connect Uptrack to Zapier — Integration Guide' },
      { name: 'description', content: 'Connect Uptrack to 6,000+ apps via Zapier using webhooks. Send uptime alerts to Google Sheets, PagerDuty, Notion, Jira, and more.' },
      { property: 'og:title', content: 'Connect Uptrack to Zapier' },
      { property: 'og:description', content: 'Connect Uptrack to 6,000+ apps via Zapier using webhooks. Send uptime alerts to Google Sheets, PagerDuty, Notion, Jira, and more.' },
      { property: 'og:image', content: 'https://uptrack.app/og/guides-zapier.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/guides/zapier' },
    ],
    scripts: [
      jsonLdScript(
        article({
          headline: 'Connect Uptrack to Zapier: Integration Guide',
          description: 'Connect Uptrack to 6,000+ apps via Zapier using webhooks. Send uptime alerts to Google Sheets, PagerDuty, Notion, Jira, and more.',
          url: 'https://uptrack.app/guides/zapier',
          datePublished: '2026-03-31',
          imageUrl: 'https://uptrack.app/og/guides-zapier.png',
        }),
      ),
    ],
  }),
})

function ZapierGuidePage() {
  return (
    <>

      <>
        <div className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <h1 className="font-sans mb-4 text-4xl font-semibold">Connect Uptrack to Zapier</h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            Send Uptrack alerts to 6,000+ apps — Google Sheets, PagerDuty, Notion, Jira, Trello, and more. No code required.
          </p>

          {/* How it works */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">How it works</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Uptrack sends a JSON webhook to a URL you specify. Zapier's "Webhooks by Zapier" trigger catches it and routes it to any app.
            </p>
            <div className="rounded-lg border bg-muted/50 p-6">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">1</span>
                <span>Monitor goes down → Uptrack fires webhook</span>
              </div>
              <div className="ml-4 border-l-2 border-muted-foreground/20 py-2 pl-8" />
              <div className="flex items-center gap-4 text-sm">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">2</span>
                <span>Zapier catches the webhook</span>
              </div>
              <div className="ml-4 border-l-2 border-muted-foreground/20 py-2 pl-8" />
              <div className="flex items-center gap-4 text-sm">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">3</span>
                <span>Zapier sends it to Google Sheets, PagerDuty, Jira, etc.</span>
              </div>
            </div>
          </section>

          {/* Step by step */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Step-by-step setup</h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-sans mb-2 text-lg font-medium">1. Create a Zap in Zapier</h3>
                <p className="text-lg leading-[1.7] text-muted-foreground">
                  Go to <a href="https://zapier.com/app/zaps" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">zapier.com</a> and create a new Zap. For the trigger, search for <strong>"Webhooks by Zapier"</strong> and select <strong>"Catch Hook"</strong>.
                </p>
              </div>

              <div>
                <h3 className="font-sans mb-2 text-lg font-medium">2. Copy the Zapier webhook URL</h3>
                <p className="text-lg leading-[1.7] text-muted-foreground">
                  Zapier will give you a unique webhook URL like:
                </p>
                <pre tabIndex={0} className="mt-2 rounded-md bg-muted p-3 text-sm font-mono overflow-x-auto">
                  https://hooks.zapier.com/hooks/catch/12345/abcdef/
                </pre>
                <p className="mt-2 text-lg leading-[1.7] text-body">Copy this URL.</p>
              </div>

              <div>
                <h3 className="font-sans mb-2 text-lg font-medium">3. Create a webhook alert channel in Uptrack</h3>
                <p className="text-lg leading-[1.7] text-muted-foreground">
                  Go to <strong>Dashboard → Alerts → Add Channel</strong>. Select <strong>"Webhook"</strong> as the type and paste the Zapier URL.
                </p>
              </div>

              <div>
                <h3 className="font-sans mb-2 text-lg font-medium">4. Test the connection</h3>
                <p className="text-lg leading-[1.7] text-muted-foreground">
                  Click <strong>"Test"</strong> on your new webhook channel. Go back to Zapier and click "Test trigger" — you should see the test data from Uptrack.
                </p>
              </div>

              <div>
                <h3 className="font-sans mb-2 text-lg font-medium">5. Add your action</h3>
                <p className="text-lg leading-[1.7] text-muted-foreground">
                  Choose what Zapier does with the alert. Popular options:
                </p>
                <ul className="mt-2 space-y-1 text-lg leading-[1.7] text-body">
                  <li>• <strong>Google Sheets</strong> — Log every incident to a spreadsheet</li>
                  <li>• <strong>PagerDuty</strong> — Create an incident in PagerDuty</li>
                  <li>• <strong>Jira</strong> — Create a bug ticket automatically</li>
                  <li>• <strong>Notion</strong> — Add to an incident database</li>
                  <li>• <strong>Trello</strong> — Create a card in your ops board</li>
                  <li>• <strong>SMS (Twilio)</strong> — Send a text message</li>
                  <li>• <strong>Email (Gmail)</strong> — Send to a custom email</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Webhook payload */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Webhook payload</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              When an incident happens, Uptrack sends this JSON to your webhook URL:
            </p>
            <pre tabIndex={0} className="rounded-md bg-muted p-4 text-sm font-mono overflow-x-auto">
{`{
  "event": "incident.created",
  "monitor": {
    "name": "API Production",
    "url": "https://api.example.com",
    "type": "http"
  },
  "incident": {
    "status": "ongoing",
    "cause": "HTTP 500 Internal Server Error",
    "started_at": "2026-03-31T10:15:00Z"
  }
}`}
            </pre>
            <p className="mt-4 text-lg leading-[1.7] text-body">
              When the incident resolves, a second webhook fires with <code className="rounded bg-muted px-1">"event": "incident.resolved"</code> and includes <code className="rounded bg-muted px-1">duration</code> in seconds.
            </p>
          </section>

          {/* CTA */}
          <section className="rounded-lg border bg-muted/50 p-8 text-center">
            <h2 className="font-sans mb-2 text-xl font-semibold">Ready to connect?</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Webhooks are available on every paid plan.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild>
                <Link to="/pricing">
                  See plans <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </>

      </>
  )
}
